import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import App from "./App";
import { AccountSetupPage } from "./features/auth/AccountSetupPage";
import { LoginPage } from "./features/auth/LoginPage";
import { MfaStepUpBoundary } from "./features/auth/MfaStepUpBoundary";
import { PasswordRecoveryPage } from "./features/auth/PasswordRecoveryPage";
import { RequiredPasswordChangePage } from "./features/auth/RequiredPasswordChangePage";
import { useAuthCopy } from "./features/auth/authCatalog";
import { useWorkspaceCopy } from "./i18n/workspaceCatalog";
import { NAVIGATION_EVENT, currentPath, pushNavigation } from "./navigation";

const AdminApp = lazy(async () => {
  const module = await import("./features/admin/AdminApp");
  return { default: module.AdminApp };
});

const ClientApp = lazy(async () => {
  const module = await import("./features/client/ClientApp");
  return { default: module.ClientApp };
});

const AnalystWorkspaceRouter = lazy(async () => {
  const module = await import("./features/analyst/AnalystWorkspaceRouter");
  return { default: module.AnalystWorkspaceRouter };
});

const SeniorWorkspaceRouter = lazy(async () => {
  const module = await import("./features/senior/SeniorWorkspaceRouter");
  return { default: module.SeniorWorkspaceRouter };
});

const OrganizationManagerRouter = lazy(async () => {
  const module = await import("./features/organization/OrganizationManagerRouter");
  return { default: module.OrganizationManagerRouter };
});

const LOGIN_ENTRY_SELECTOR = ".public-site .header-actions .button.secondary.small";

export function Root() {
  const { t } = useAuthCopy();
  const { t: workspaceT } = useWorkspaceCopy();
  const [path, setPath] = useState(currentPath);

  const navigate = useCallback((nextPath: string) => {
    pushNavigation(nextPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const sync = () => setPath(currentPath());
    window.addEventListener("popstate", sync);
    window.addEventListener(NAVIGATION_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(NAVIGATION_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    if (path === "/login") return;

    const enableLoginEntry = () => {
      const entry = document.querySelector<HTMLButtonElement>(LOGIN_ENTRY_SELECTOR);
      if (!entry) return;
      entry.dataset.loginEntry = "true";
      entry.setAttribute("aria-disabled", "false");
      entry.title = t("login.title");
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>(LOGIN_ENTRY_SELECTOR) : null;
      if (!target) return;
      event.preventDefault();
      navigate("/login");
    };

    enableLoginEntry();
    const observer = new MutationObserver(enableLoginEntry);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    document.addEventListener("click", onClick, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("click", onClick, true);
    };
  }, [navigate, path, t]);

  const activationToken = path === "/activate" ? new URLSearchParams(window.location.search).get("token") ?? undefined : undefined;

  if (path === "/login") return <LoginPage onBack={() => navigate("/")} onPasswordRecovery={() => navigate("/password-recovery")} onAuthenticated={(actor) => navigate(actor.setup_required ? "/setup" : actor.requires_password_change ? "/change-password" : actor.workspace_home)} />;
  if (path === "/password-recovery") return <PasswordRecoveryPage token={new URLSearchParams(window.location.search).get("token") ?? undefined} onBackToLogin={() => navigate("/login")} />;
  if (path === "/activate") return <AccountSetupPage activationToken={activationToken ?? ""} onComplete={navigate} onReturnToLogin={() => navigate("/login")} />;
  if (path === "/setup") return <AccountSetupPage onComplete={navigate} onReturnToLogin={() => navigate("/login")} />;
  if (path === "/change-password") return <RequiredPasswordChangePage onComplete={() => navigate("/login")} onSessionUnavailable={() => navigate("/login")} />;
  if (path === "/admin" || path.startsWith("/admin/")) return <MfaStepUpBoundary onSessionExpired={() => navigate("/login")}><Suspense fallback={<main className="admin-route-state">{t("route.openAdmin")}</main>}><AdminApp path={path} onNavigate={navigate} onSignedOut={() => navigate("/login")} /></Suspense></MfaStepUpBoundary>;
  if (path === "/client" || path.startsWith("/client/")) return <MfaStepUpBoundary onSessionExpired={() => navigate("/login")}><Suspense fallback={<main className="admin-route-state">{workspaceT("route.client.workspace")}</main>}><ClientApp path={path} onNavigate={navigate} onSignedOut={() => navigate("/login")} /></Suspense></MfaStepUpBoundary>;
  if (path === "/analyst" || path.startsWith("/analyst/")) return <MfaStepUpBoundary onSessionExpired={() => navigate("/login")}><Suspense fallback={<main className="admin-route-state">{workspaceT("route.analyst.workspace")}</main>}><AnalystWorkspaceRouter path={path} onNavigate={navigate} onSignedOut={() => navigate("/login")} /></Suspense></MfaStepUpBoundary>;
  if (path === "/senior" || path.startsWith("/senior/")) return <MfaStepUpBoundary onSessionExpired={() => navigate("/login")}><Suspense fallback={<main className="admin-route-state">{t("route.openSenior")}</main>}><SeniorWorkspaceRouter path={path} onNavigate={navigate} onSignedOut={() => navigate("/login")} /></Suspense></MfaStepUpBoundary>;
  if (path === "/organization" || path.startsWith("/organization/")) return <MfaStepUpBoundary onSessionExpired={() => navigate("/login")}><Suspense fallback={<main className="admin-route-state">{workspaceT("route.organization.workspace")}</main>}><OrganizationManagerRouter path={path} onNavigate={navigate} /></Suspense></MfaStepUpBoundary>;
  return <App />;
}
